import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Alert,
  Badge,
  Button,
  Form,
  Modal,
  Spinner,
  Table,
} from 'react-bootstrap';
import { ArrowDownUp, Download, Search } from 'react-bootstrap-icons';
import Papa from 'papaparse';
import axios from '../axiosAlertConfig';

const formatDateTime = (value) => {
  if (!value) return '';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);

  return parsed.toLocaleString();
};

const cellValue = (value) => {
  const text = String(value ?? '').trim();
  return text || '—';
};

const compareText = (left, right) => String(left || '').localeCompare(
  String(right || ''),
  undefined,
  { sensitivity: 'base' },
);

const HIDDEN_ADMIN_PREFIX = String(
  process.env.REACT_APP_HIDDEN_ADMIN_PREFIX || '',
).trim().toLowerCase();

const isHiddenAdminRow = (row) => {
  if (!HIDDEN_ADMIN_PREFIX) return false;

  return [row?.username, row?.userID].some((value) => String(value || '')
    .trim()
    .toLowerCase()
    .includes(HIDDEN_ADMIN_PREFIX));
};

const summarizeRows = (reportRows) => ({
  totalUsers: reportRows.length,
  passwordSetCount: reportRows.filter((row) => Boolean(row.passwordSet)).length,
  passwordNotSetCount: reportRows.filter((row) => !row.passwordSet).length,
  loggedInCount: reportRows.filter((row) => Boolean(row.loginDate)).length,
  neverLoggedInCount: reportRows.filter((row) => !row.loginDate).length,
  incidentLoginCount: reportRows.filter((row) => Boolean(row.incidentLoginDate)).length,
  participatedCount: reportRows.filter((row) => Boolean(row.participated)).length,
});

export default function IncidentUserReadinessModal({
  show,
  onHide,
  incident,
}) {
  const incidentID = String(incident?.incidentID || '').trim();

  const [rows, setRows] = useState([]);
  const [reportIncident, setReportIncident] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [loginSortOrder, setLoginSortOrder] = useState('logged-in-first');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadReport = useCallback(async () => {
    if (!incidentID) {
      setRows([]);
      setReportIncident(null);
      setError('No incident was selected.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setRows([]);
      setReportIncident(null);

      const { data } = await axios.get(
        `/ialert/incidents/${encodeURIComponent(incidentID)}/user-readiness-report`,
      );

      const reportRows = Array.isArray(data?.rows) ? data.rows : [];
      setRows(reportRows.filter((row) => !isHiddenAdminRow(row)));
      setReportIncident(data?.incident || null);
    } catch (requestError) {
      console.error('Failed to load user login and incident activity report', requestError);
      setRows([]);
      setReportIncident(null);
      setError(
        requestError?.response?.data?.message ||
        'Failed to load the user login and incident activity report.',
      );
    } finally {
      setLoading(false);
    }
  }, [incidentID]);

  useEffect(() => {
    if (!show) return;

    setSearchText('');
    setLoginSortOrder('logged-in-first');
    void loadReport();
  }, [show, loadReport]);

  const summary = useMemo(() => summarizeRows(rows), [rows]);

  const displayedRows = useMemo(() => {
    const needle = searchText.trim().toLowerCase();

    const filtered = needle
      ? rows.filter((row) => {
        const values = [
          row.FirstName,
          row.LastName,
          row.userID,
          row.Date,
          formatDateTime(row.Date),
          row.loginDate,
          formatDateTime(row.loginDate),
          row.lastLoginDate,
          formatDateTime(row.lastLoginDate),
          row.incidentLoginDate,
          formatDateTime(row.incidentLoginDate),
          row.IncidentType,
          row.IncidentCategory,
          row.IncidentDate,
          formatDateTime(row.IncidentDate),
          row.passwordSet,
          row.incidentLocation,
          row.IncidentStatus,
          row.participationDate,
          formatDateTime(row.participationDate),
          row.participated ? 'participated' : 'did not participate',
          row.incidentLoginDate ? 'logged in during incident' : 'no incident login',
          row.loginDate ? 'logged in' : 'never logged in',
          row.passwordSet ? 'password set' : 'password not set',
        ];

        return values
          .map((value) => String(value ?? '').toLowerCase())
          .join(' ')
          .includes(needle);
      })
      : rows;

    return [...filtered].sort((left, right) => {
      const leftHasLogin = left.loginDate ? 1 : 0;
      const rightHasLogin = right.loginDate ? 1 : 0;
      if (leftHasLogin !== rightHasLogin) {
        return loginSortOrder === 'logged-in-first'
          ? rightHasLogin - leftHasLogin
          : leftHasLogin - rightHasLogin;
      }

      const leftHasPassword = left.passwordSet ? 1 : 0;
      const rightHasPassword = right.passwordSet ? 1 : 0;
      if (leftHasPassword !== rightHasPassword) {
        return leftHasPassword - rightHasPassword;
      }

      const lastNameCompare = compareText(left.LastName, right.LastName);
      if (lastNameCompare !== 0) return lastNameCompare;

      return compareText(left.FirstName, right.FirstName);
    });
  }, [rows, searchText, loginSortOrder]);

  const exportCsv = () => {
    if (!displayedRows.length) return;

    const csvRows = displayedRows.map((row) => ({
      FirstName: row.FirstName || '',
      LastName: row.LastName || '',
      userID: row.userID || '',
      Date: formatDateTime(row.Date),
      loginDate: formatDateTime(row.loginDate),
      lastLoginDate: formatDateTime(row.lastLoginDate),
      incidentLoginDate: formatDateTime(row.incidentLoginDate),
      IncidentType: row.IncidentType || '',
      IncidentCategory: row.IncidentCategory || '',
      IncidentDate: formatDateTime(row.IncidentDate),
      passwordSet: row.passwordSet || '',
      incidentLocation: row.incidentLocation || '',
      IncidentStatus: row.IncidentStatus || '',
      participationDate: formatDateTime(row.participationDate),
      participated: row.participated ? 'Yes' : '',
    }));

    const csv = Papa.unparse(csvRows, { escapeFormulae: true });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `incident_${incidentID}_user_login_activity.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const incidentLabel = [
    reportIncident?.IncidentType || incident?.incidentType,
    reportIncident?.IncidentCategory || incident?.incidentCategory,
  ].filter(Boolean).join(' — ');

  return (
    <Modal
      show={show}
      onHide={onHide}
      fullscreen
      scrollable
    >
      <Modal.Header closeButton>
        <div>
          <Modal.Title>User Login &amp; Incident Activity</Modal.Title>
          <div className="small text-muted mt-1">
            {incidentLabel || `Incident ${incidentID}`}
            {(reportIncident?.buildingName || incident?.buildingName)
              ? ` • ${reportIncident?.buildingName || incident?.buildingName}`
              : ''}
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="d-flex flex-column" style={{ minHeight: 0 }}>
        {error ? (
          <Alert variant="danger" className="d-flex align-items-center justify-content-between gap-3">
            <span>{error}</span>
            <Button variant="outline-danger" size="sm" onClick={() => void loadReport()}>
              Retry
            </Button>
          </Alert>
        ) : null}

        <div className="d-flex flex-wrap gap-2 mb-3">
          <Badge bg="secondary">Users: {Number(summary.totalUsers || 0)}</Badge>
          <Badge bg="success">Password Set: {Number(summary.passwordSetCount || 0)}</Badge>
          <Badge bg="warning" text="dark">
            Password Not Set: {Number(summary.passwordNotSetCount || 0)}
          </Badge>
          <Badge bg="primary">Ever Logged In: {Number(summary.loggedInCount || 0)}</Badge>
          <Badge bg="danger">Never Logged In: {Number(summary.neverLoggedInCount || 0)}</Badge>
          <Badge bg="info" text="dark">
            Logged In During Incident: {Number(summary.incidentLoginCount || 0)}
          </Badge>
          <Badge bg="dark">Incident Participants: {Number(summary.participatedCount || 0)}</Badge>
        </div>

        <div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center gap-2 mb-3">
          <div className="position-relative flex-grow-1">
            <Search
              size={16}
              className="position-absolute top-50 translate-middle-y text-muted"
              style={{ left: 12, pointerEvents: 'none' }}
            />
            <Form.Control
              type="search"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              placeholder="Search name, user ID, dates, incident, location, or status"
              style={{ paddingLeft: 36 }}
              aria-label="Search user login and incident activity"
            />
          </div>

          <Button
            variant="outline-secondary"
            onClick={() => setLoginSortOrder((current) => (
              current === 'logged-in-first'
                ? 'not-logged-in-first'
                : 'logged-in-first'
            ))}
            disabled={loading || rows.length === 0}
            className="d-inline-flex align-items-center justify-content-center gap-1"
            aria-pressed={loginSortOrder === 'not-logged-in-first'}
            title="Toggle whether logged-in or not-logged-in users appear first"
          >
            <ArrowDownUp size={16} />
            {loginSortOrder === 'logged-in-first'
              ? 'Logged In First'
              : 'Not Logged In First'}
          </Button>

          <Button
            variant="outline-primary"
            onClick={exportCsv}
            disabled={loading || displayedRows.length === 0}
            className="d-inline-flex align-items-center justify-content-center gap-1"
          >
            <Download size={16} />
            Export CSV
          </Button>
        </div>

        <div className="small text-muted mb-2">
          Showing {displayedRows.length} of {rows.length} users.{' '}
          {loginSortOrder === 'logged-in-first'
            ? 'Logged-in users are shown first.'
            : 'Users who have not logged in are shown first.'}
        </div>

        {loading ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner animation="border" role="status" />
            <span className="ms-2">Loading report…</span>
          </div>
        ) : (
          <div className="flex-grow-1" style={{ minHeight: 0, overflow: 'auto' }}>
            <Table
              bordered
              hover
              size="sm"
              className="mb-0 align-middle"
              style={{ minWidth: 2050 }}
            >
              <thead
                className="table-light"
                style={{ position: 'sticky', top: 0, zIndex: 2 }}
              >
                <tr>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th>User ID</th>
                  <th>Account Created</th>
                  <th>Password Set</th>
                  <th>First Login</th>
                  <th>Last Login</th>
                  <th>Incident Login</th>
                  <th>Incident Type</th>
                  <th>Incident Category</th>
                  <th>Incident Date</th>
                  <th>Incident Location</th>
                  <th>Incident Status</th>
                  <th>Activity Date</th>
                </tr>
              </thead>

              <tbody>
                {displayedRows.map((row) => (
                  <tr
                    key={row.userID}
                    className={!row.loginDate ? 'table-warning' : ''}
                  >
                    <td>{cellValue(row.FirstName)}</td>
                    <td>{cellValue(row.LastName)}</td>
                    <td>{cellValue(row.userID)}</td>
                    <td>{cellValue(formatDateTime(row.Date))}</td>
                    <td>
                      {row.passwordSet ? (
                        <Badge bg="success">Yes</Badge>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>{cellValue(formatDateTime(row.loginDate))}</td>
                    <td>{cellValue(formatDateTime(row.lastLoginDate))}</td>
                    <td>{cellValue(formatDateTime(row.incidentLoginDate))}</td>
                    <td>{cellValue(row.IncidentType)}</td>
                    <td>{cellValue(row.IncidentCategory)}</td>
                    <td>{cellValue(formatDateTime(row.IncidentDate))}</td>
                    <td style={{ whiteSpace: 'normal', minWidth: 180 }}>
                      {cellValue(row.incidentLocation)}
                    </td>
                    <td style={{ whiteSpace: 'normal', minWidth: 260 }}>
                      {cellValue(row.IncidentStatus)}
                    </td>
                    <td>{cellValue(formatDateTime(row.participationDate))}</td>
                  </tr>
                ))}

                {!displayedRows.length && !error ? (
                  <tr>
                    <td colSpan={14} className="text-center text-muted py-4">
                      No users match the current search.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </Table>
          </div>
        )}

        <div className="small text-muted mt-3">
          Account Created comes from the Users record. Password Set reflects whether the Users record contains a password hash. Incident Login is the first successful login recorded while the incident was active. Incident Location and Incident Status are the latest non-empty values submitted for this incident.
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}
